import { useState, useEffect } from 'react';
import { adminAPI, catalogAPI } from '../services/api';
import { fmt } from '../utils/format';
import Sidebar from '../components/layout/Sidebar';
import toast from 'react-hot-toast';

export default function CatalogPage() {
  const [tab, setTab] = useState('params');

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div><h2>Products &amp; Pricing</h2><div className="page-subtitle">Admin catalog management</div></div>
        </div>
        <div className="page-body">
          <div className="tabs">
            {[
              ['params',  'Global Params'],
              ['models',  'ALU Models'],
              ['glass',   'Glass Prices'],
              ['iron',    'Iron Catalog'],
            ].map(([key, label]) => (
              <button key={key} className={`tab${tab===key?' active':''}`} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>

          {tab === 'params'  && <ParamsTab />}
          {tab === 'models'  && <ModelsTab />}
          {tab === 'glass'   && <GlassTab />}
          {tab === 'iron'    && <IronCatalogTab />}
        </div>
      </div>
    </div>
  );
}

// ── GLOBAL PARAMS ────────────────────────────────────────────
function ParamsTab() {
  const [params,  setParams]  = useState(null);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    adminAPI.getPriceParams().then(r => {
      setParams(r.data);
      setForm({
        ddp_pct:   (parseFloat(r.data.ddp_pct)   * 100).toFixed(1),
        cm_pct:    (parseFloat(r.data.cm_pct)    * 100).toFixed(1),
        labor_pct: (parseFloat(r.data.labor_pct) * 100).toFixed(1),
        curve_a:   r.data.curve_a,
        curve_b:   r.data.curve_b,
        notes:     '',
      });
    });
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updatePriceParams({
        ddp_pct:   parseFloat(form.ddp_pct)   / 100,
        cm_pct:    parseFloat(form.cm_pct)    / 100,
        labor_pct: parseFloat(form.labor_pct) / 100,
        curve_a:   parseFloat(form.curve_a),
        curve_b:   parseFloat(form.curve_b),
        notes:     form.notes,
      });
      toast.success('Parameters updated — new version created');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!params) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      <div className="card-header">
        <span className="card-title">Global Pricing Parameters</span>
        <span className="text-muted text-sm">Changes create a new versioned record</span>
      </div>

      <div style={{ background: 'var(--bg-3)', borderRadius: 8, padding: 12, marginBottom: 20, fontSize: 12 }}>
        <span className="text-muted">Formula: </span>
        <code style={{ color: 'var(--accent)' }}>Rec $/sf = MAX(Floor, (curve_a + curve_b ÷ Area) × Factor)</code>
      </div>

      <div className="form-row-3">
        <div className="form-group">
          <label className="form-label">DDP % (logistics cost)</label>
          <input className="form-input text-mono" type="number" step="0.1" value={form.ddp_pct}
            onChange={e => set('ddp_pct', e.target.value)} />
          <div className="form-hint">Currently {form.ddp_pct}% — DDP = EXW × (1 + DDP%)</div>
        </div>
        <div className="form-group">
          <label className="form-label">Target CM %</label>
          <input className="form-input text-mono" type="number" step="0.1" value={form.cm_pct}
            onChange={e => set('cm_pct', e.target.value)} />
          <div className="form-hint">Sale = DDP ÷ (1 − CM%)</div>
        </div>
        <div className="form-group">
          <label className="form-label">Labor % (internal)</label>
          <input className="form-input text-mono" type="number" step="0.1" value={form.labor_pct}
            onChange={e => set('labor_pct', e.target.value)} />
          <div className="form-hint">Install = Net Sales × Labor%</div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Curve a (base intercept)</label>
          <input className="form-input text-mono" type="number" step="1" value={form.curve_a}
            onChange={e => set('curve_a', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Curve b (slope)</label>
          <input className="form-input text-mono" type="number" step="1" value={form.curve_b}
            onChange={e => set('curve_b', e.target.value)} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Change note (optional)</label>
        <input className="form-input" placeholder="e.g. Updated DDP% due to new freight rates"
          value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save New Version'}
      </button>
    </div>
  );
}

// ── ALU MODELS ───────────────────────────────────────────────
function ModelsTab() {
  const [models,  setModels]  = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    catalogAPI.aluModels().then(r => setModels(r.data)).finally(() => setLoading(false));
  }, []);

  const handleSave = async (m, factor, description) => {
    try {
      await adminAPI.updateAluModel(m.id, { factor: parseFloat(factor), description, active: m.active });
      toast.success(`${m.code} updated`);
      setEditing(null);
      catalogAPI.aluModels().then(r => setModels(r.data));
    } catch { toast.error('Save failed'); }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Model</th><th>Factor</th><th>Rec $/sf (20 sqft ex.)</th><th>Description</th><th></th></tr>
          </thead>
          <tbody>
            {models.map(m => {
              const rec20 = Math.max(55, (30 + 100 / 20) * m.factor);
              return (
                <tr key={m.id}>
                  <td><span className="badge badge-gray">{m.code}</span></td>
                  <td className="text-mono" style={{ fontWeight: 600 }}>{m.factor}</td>
                  <td className="text-mono">{fmt.currency(rec20)}/sf</td>
                  <td className="text-muted text-sm">{m.description}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(m)}>Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <ModelEditModal model={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function ModelEditModal({ model, onClose, onSave }) {
  const [factor, setFactor] = useState(model.factor);
  const [desc,   setDesc]   = useState(model.description || '');
  const recPrice = Math.max(55, (30 + 100 / 20) * parseFloat(factor || 1));

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">Edit Model — {model.code}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="form-group">
          <label className="form-label">Factor (80S = 1.00 base)</label>
          <input className="form-input text-mono" type="number" step="0.01" value={factor}
            onChange={e => setFactor(e.target.value)} />
          <div className="form-hint">
            Rec price on 20 sqft: <strong>{fmt.currency(recPrice)}/sf</strong>
            {' '}({parseFloat(factor) > 1 ? '+' : ''}{((parseFloat(factor)-1)*100).toFixed(0)}% vs 80S)
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-input" value={desc} onChange={e => setDesc(e.target.value)} />
        </div>
        <div className="flex justify-between" style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(model, factor, desc)}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── GLASS PRICES ─────────────────────────────────────────────
function GlassTab() {
  const [prices,     setPrices]    = useState([]);
  const [curved,     setCurved]    = useState([]);
  const [glassTypes, setGlassTypes]= useState([]);
  const [thicknesses,setThick]     = useState([]);
  const [vendors,    setVendors]   = useState([]);
  const [ranges,     setRanges]    = useState([]);
  const [showFlat,   setShowFlat]  = useState(false);
  const [showCurved, setShowCurved]= useState(false);
  const [loading,    setLoading]   = useState(true);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      adminAPI.getGlassPricesFlat(),
      adminAPI.getGlassPricesCurved(),
      catalogAPI.glassTypes(),
      catalogAPI.glassThicknesses(),
      catalogAPI.glassVendors(),
      catalogAPI.glassSizeRanges(),
    ]).then(([pf, pc, gt, th, v, r]) => {
      setPrices(pf.data); setCurved(pc.data);
      setGlassTypes(gt.data); setThick(th.data);
      setVendors(v.data); setRanges(r.data);
    }).finally(() => setLoading(false));
  };
  useEffect(loadAll, []);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      {/* FLAT PRICES */}
      <div className="card" style={{ marginBottom: 20, padding: 0 }}>
        <div className="card-header" style={{ padding: '16px 20px' }}>
          <span className="card-title">Flat Glass Prices</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowFlat(true)}>+ Add Price</button>
        </div>
        {prices.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 0' }}>
            <h3>No flat glass prices yet</h3>
            <p>Add prices using the button above. You'll need the supplier packet for exact values.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Supplier</th><th>Type</th><th>Thickness</th><th>Size Range</th><th>Cuts</th><th>$/sqft</th><th>Min Charge</th></tr>
              </thead>
              <tbody>
                {prices.map(p => (
                  <tr key={p.id}>
                    <td>{p.vendor_name}</td>
                    <td>{p.glass_name}</td>
                    <td className="text-mono">{p.thickness}</td>
                    <td className="text-muted text-sm">{p.size_range || 'All sizes'}</td>
                    <td className="text-mono">{p.cuts_exact ?? 'Any'}</td>
                    <td className="text-mono" style={{ fontWeight: 600 }}>{fmt.currency(p.price_per_sqft)}</td>
                    <td className="text-mono">{p.min_charge ? fmt.currency(p.min_charge) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CURVED PRICES */}
      <div className="card" style={{ padding: 0 }}>
        <div className="card-header" style={{ padding: '16px 20px' }}>
          <span className="card-title">Curved Glass Prices</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCurved(true)}>+ Add Curved Price</button>
        </div>
        {curved.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 0' }}>
            <h3>No curved glass prices yet</h3>
            <p>Curved glass has its own independent price table.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Supplier</th><th>Type</th><th>Thickness</th><th>Radius Range (in)</th><th>$/sqft</th><th>Lead Time</th></tr>
              </thead>
              <tbody>
                {curved.map(p => (
                  <tr key={p.id}>
                    <td>{p.vendor_name}</td>
                    <td>{p.glass_name}</td>
                    <td className="text-mono">{p.thickness}</td>
                    <td className="text-mono">
                      {p.radius_min_in ? `${p.radius_min_in}"` : '—'} – {p.radius_max_in ? `${p.radius_max_in}"` : '∞'}
                    </td>
                    <td className="text-mono" style={{ fontWeight: 600 }}>{fmt.currency(p.price_per_sqft)}</td>
                    <td className="text-muted">{p.lead_time_days ? `${p.lead_time_days} days` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showFlat && (
        <GlassPriceModal
          isCurved={false}
          glassTypes={glassTypes} thicknesses={thicknesses} vendors={vendors} ranges={ranges}
          onClose={() => setShowFlat(false)}
          onSaved={() => { setShowFlat(false); loadAll(); }}
        />
      )}
      {showCurved && (
        <GlassPriceModal
          isCurved={true}
          glassTypes={glassTypes} thicknesses={thicknesses} vendors={vendors} ranges={ranges}
          onClose={() => setShowCurved(false)}
          onSaved={() => { setShowCurved(false); loadAll(); }}
        />
      )}
    </div>
  );
}

function GlassPriceModal({ isCurved, glassTypes, thicknesses, vendors, ranges, onClose, onSaved }) {
  const [form, setForm] = useState({
    vendor_id: '', glass_type_id: '', thickness_id: '', size_range_id: '',
    cuts_exact: '', price_per_sqft: '', min_charge: '', notes: '',
    radius_min_in: '', radius_max_in: '', lead_time_days: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.vendor_id || !form.glass_type_id || !form.thickness_id || !form.price_per_sqft)
      return toast.error('Supplier, type, thickness, and price are required');
    setSaving(true);
    try {
      const payload = {
        vendor_id:      parseInt(form.vendor_id),
        glass_type_id:  parseInt(form.glass_type_id),
        thickness_id:   parseInt(form.thickness_id),
        size_range_id:  form.size_range_id  ? parseInt(form.size_range_id)  : null,
        cuts_exact:     form.cuts_exact     ? parseInt(form.cuts_exact)     : null,
        price_per_sqft: parseFloat(form.price_per_sqft),
        min_charge:     form.min_charge     ? parseFloat(form.min_charge)   : null,
        notes:          form.notes          || null,
        radius_min_in:  form.radius_min_in  ? parseFloat(form.radius_min_in): null,
        radius_max_in:  form.radius_max_in  ? parseFloat(form.radius_max_in): null,
        lead_time_days: form.lead_time_days ? parseInt(form.lead_time_days) : null,
      };
      if (isCurved) await adminAPI.createGlassPriceCurved(payload);
      else          await adminAPI.createGlassPriceFlat(payload);
      toast.success('Price added');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">Add {isCurved ? 'Curved' : 'Flat'} Glass Price</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Supplier *</label>
            <select className="form-select" value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}>
              <option value="">Select…</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Glass Type *</label>
            <select className="form-select" value={form.glass_type_id} onChange={e => set('glass_type_id', e.target.value)}>
              <option value="">Select…</option>
              {glassTypes.filter(g => g.is_curved === isCurved).map(g =>
                <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Thickness *</label>
            <select className="form-select" value={form.thickness_id} onChange={e => set('thickness_id', e.target.value)}>
              <option value="">Select…</option>
              {thicknesses.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Size Range</label>
            <select className="form-select" value={form.size_range_id} onChange={e => set('size_range_id', e.target.value)}>
              <option value="">All sizes (no range filter)</option>
              {ranges.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row-3">
          <div className="form-group">
            <label className="form-label">Price $/sqft *</label>
            <input className="form-input text-mono" type="number" step="0.01" placeholder="0.00"
              value={form.price_per_sqft} onChange={e => set('price_per_sqft', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Min Charge $</label>
            <input className="form-input text-mono" type="number" step="0.01"
              value={form.min_charge} onChange={e => set('min_charge', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Cuts (exact)</label>
            <input className="form-input text-mono" type="number" min="0" placeholder="any"
              value={form.cuts_exact} onChange={e => set('cuts_exact', e.target.value)} />
          </div>
        </div>

        {isCurved && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Radius Min (in)</label>
              <input className="form-input text-mono" type="number" step="0.5"
                value={form.radius_min_in} onChange={e => set('radius_min_in', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Radius Max (in)</label>
              <input className="form-input text-mono" type="number" step="0.5"
                value={form.radius_max_in} onChange={e => set('radius_max_in', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Lead Time (days)</label>
              <input className="form-input text-mono" type="number"
                value={form.lead_time_days} onChange={e => set('lead_time_days', e.target.value)} />
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <div className="flex justify-between" style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Add Price'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── IRON CATALOG (read-only view for now) ────────────────────
function IronCatalogTab() {
  const [designs,  setDesigns]  = useState([]);
  const [cats,     setCats]     = useState([]);
  const [selCat,   setSelCat]   = useState('');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      catalogAPI.ironCategories(),
      catalogAPI.ironDesigns(),
    ]).then(([c, d]) => { setCats(c.data); setDesigns(d.data); })
    .finally(() => setLoading(false));
  }, []);

  const filtered = selCat ? designs.filter(d => d.category_id === parseInt(selCat)) : designs;

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="card-header" style={{ padding: '16px 20px' }}>
        <span className="card-title">Iron Door Designs ({filtered.length})</span>
        <select className="form-select" style={{ width: 220 }} value={selCat} onChange={e => setSelCat(e.target.value)}>
          <option value="">All Categories</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Category</th><th>Design</th><th>List $/sf</th><th>Image</th></tr>
          </thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.id}>
                <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{d.category_name}</span></td>
                <td style={{ color: 'var(--text)', fontWeight: 500 }}>{d.name}</td>
                <td className="text-mono" style={{ fontWeight: 600 }}>{fmt.currency(d.list_price_sf)}/sf</td>
                <td>
                  {d.image_url
                    ? <img src={d.image_url} alt={d.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                    : <span className="text-muted text-sm">No photo</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
